import Camera from '@FreeRider/Camera';
import Screen from '@FreeRider/Screen';
import Track from '@FreeRider/Track';
import Player from '@FreeRider/Player/Player';
import CanvasPool from '@FreeRider/Utils/CanvasPool';
import TrackRenderer from '@FreeRider/Renderers/TrackRenderer';
import PlayerRenderer from '@FreeRider/Renderers/PlayerRenderer';
import global from '@Global';

/**
 * Core FreeRider game class
 */
class FreeRider
{
    /*---------- Settings ---------------- */
    private readonly FPS: number = 30;

    /*------------------------------------ */
    private container: HTMLElement;
    private canvas: HTMLCanvasElement;
    private context2d: CanvasRenderingContext2D;
    private camera: Camera;
    private screen: Screen;
    private track: Track;
    private player: Player;

    /*----------- Renderers -------------- */
    private trackRenderer: TrackRenderer;
    private playerRenderer: PlayerRenderer;
    private lastUpdateTimestamp: number;

    /**
     * Constructor
     */
    constructor()
    {
        CanvasPool.preAllocate(50);
        global.freerider = this;
    }

    /**
     * Initializes the game
     * @param canvas
     */
    public initialize(container:HTMLElement)
    {
        /**
         * Get canvas container
         */
        const canvas = CanvasPool.get();

        this.setContainer(container);
        this.setCanvas(canvas);

        container.appendChild(canvas);

        /**
         * Game Components
         */
        this.camera = new Camera();
        this.track = new Track();
        this.player = new Player();
        this.screen = new Screen();

        /**
         * Render Components
         */
        this.trackRenderer = new TrackRenderer(this.canvas);
        this.playerRenderer = new PlayerRenderer(this.canvas);

        /**
         * Tell camera to focus on player
         */
        this.camera.focusOnPlayer(this.player);

        this.lastUpdateTimestamp = 0;


    }

    /**
     * Resets the game and objects
     */
    public reset()
    {
        this.player.reset();
        this.camera.reset();
        this.camera.panToPlayer();
        this.lastUpdateTimestamp = 0;
    }

    /**
     * Updates the game
     * @param time
     * @param delta
     */
    public update()
    {
        const now = Date.now();
        const interval = 1000 / this.FPS;
        var delta = now - this.lastUpdateTimestamp
        if (delta > interval)
        {
            this.lastUpdateTimestamp = now - (delta % interval);
            this.player.update(this.track);
            this.camera.update();
        }
    }

    /**
     * Loads the game / track
     */
    public load(trackData: string)
    {
        this.track.load(trackData);
    }

    /**
     * Renders the game to the canvas
     */
    public render()
    {
        const canvas = this.canvas;
        this.context2d.clearRect(0, 0, canvas.width, canvas.height);
        this.trackRenderer.render(this.track, this.camera, this.screen);
        this.playerRenderer.render(this.player, this.camera, this.screen);
    }

    /**
     * Clears the game canvas
     */
    public clearCanvas()
    {
        const canvasWidth = this.canvas.width;
        const canvasHeight = this.canvas.height;
        this.context2d.clearRect(0, 0, canvasWidth, canvasHeight);
    }

    /**
     * Sets key down
     * @param key - keycode
     */
    public setKeyDown(key: number)
    {
        this.player.gamepad.setButtonDown(key);
    }

    /**
     * Sets key up
     * @param key - keycode
     */
    public setKeyUp(key: number)
    {
        this.player.gamepad.setButtonUp(key);
    }

    /**
     * Returns the game canvas
     */
    public getCanvas()
    {
        return this.canvas;
    }

    /**
     * Sets game container
     * @param container - container element to inject game into
     */
    public setContainer(container: HTMLElement)
    {
        this.container = container;
    }

    /**
     * Creates canvas
     * This is the main canvas in which track is drawn to
     */
    private setCanvas(canvas: HTMLCanvasElement)
    {
        const canvas2dCtx = canvas.getContext('2d');
        this.canvas = canvas;
        this.context2d = canvas2dCtx;
    }

    /**
     * Returns current player
     */
    public getPlayer()
    {
        return this.player;
    }

    /**
     * Shakes the camera for a specific duration
     */
    public shakeCamera(duration: number)
    {
        this.camera.shake(duration);
    }


    /**
     * Blurs the canvas
     * @param amount
     */
    public blur(amount: number)
    {
        this.canvas.style.filter = `blur(${amount}px)`;
    }

    /**
     *
     * @param width
     * @param height
     * @param canvasWidth
     * @param canvasHeight
     */
    public setSize(width: number, height: number, canvasWidth: string, canvasHeight: string)
    {
        const canvas = this.canvas;
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = canvasWidth;
        canvas.style.height = canvasHeight;
        this.screen.setSize(width, height);
    }

    /**
     * Shows the canvas
     */
    public show()
    {
        this.container.style.visibility = 'visible';
    }

    /**
     * Hides the canvas
     */
    public hide()
    {
        this.container.style.visibility = 'hidden';
    }
}

export default FreeRider;